<template>
  <v-container class="h-full">
    <div class="flex flex-row gap-2 text-sm">
      <router-link to="/programs">
        Programs
      </router-link>
      /
      <span>{{ localProgram?.name ?? '' }}</span>
    </div>
    <h1 class="mt-3">
      <template v-if="loading">
        Loading Program...
      </template>
      <template v-else-if="!localProgram">
        Program Not Found
      </template>
      <template v-else>
        {{ localProgram.name }} Program
      </template>
    </h1>
    <div class="flex gap-1 opacity-50 mb-4">
      <p><strong>Program ID:</strong></p>
      <p v-text="localProgram?.id ?? 'Loading...'" />
    </div>

    <div class="max-w-[1200px]">
      <v-alert
        v-if="errorMessage"
        type="error"
        variant="tonal"
        class="mb-4"
        :text="errorMessage"
        closable
        @click:close="errorMessage = ''"
      />
      <v-alert
        v-if="successMessage"
        key="success"
        type="success"
        variant="tonal"
        class="mb-4"
        :text="successMessage"
        closable
        @click:close="successMessage = ''"
      />

      <v-card color="darknavyblue" elevation="5" class="pa-4 mb-4">
        <v-card-title>Program Versions</v-card-title>
        <v-card-text>
          <v-progress-circular v-if="loading" indeterminate />
          <p v-else-if="!localProgram" class="opacity-50">
            Program not found.
          </p>
          <p v-else-if="!loadedVersions.length" class="opacity-50">
            No versions found.
          </p>
          <v-data-table
            v-else
            :headers="versionHeaders"
            :items="versionRows"
            :items-per-page="5"
            :items-per-page-options="[5, 10, 25]"
            item-value="tag"
          >
            <template #item.version="{ item }">
              <div class="py-2">
                <router-link
                  class="font-medium"
                  :to="versionRoute(item.tag)"
                >
                  v{{ item.version }}
                </router-link>
                <v-chip
                  v-if="item.env"
                  class="ml-2 font-bold"
                  size="small"
                  variant="tonal"
                  :class="envChipColor(item.env)"
                  v-text="item.env.name"
                />
                <p class="font-mono text-sm opacity-60" v-text="item.tag" />
              </div>
            </template>
            <template #item.status="{ item }">
              <template v-if="item.pin">
                <v-icon
                  v-if="item.pin.status === 'failed'"
                  key="v-icon-1"
                  v-tooltip="{
                    text: `Deployment Failed: ${item.pin.detail ?? 'Unknown error'}`,
                    location: 'bottom',
                  }"
                  icon="mdi-alert-circle-outline"
                  color="error"
                />
                <v-icon
                  v-else-if="item.pin.status === 'pending'"
                  key="v-icon-3"
                  v-tooltip="`Pending`"
                  icon="mdi-clock-outline"
                  color="warning"
                />
                <v-icon
                  v-else-if="item.pin.status === 'syncing'"
                  key="v-icon-5"
                  v-tooltip="`Syncing`"
                  icon="mdi-sync"
                  color="blue"
                />
                <v-icon
                  v-else-if="item.pin.status === 'synced'"
                  key="v-icon-2"
                  v-tooltip="`Version Pinned`"
                  icon="mdi-pin"
                  color="success"
                />
                <span v-else key="v-icon-4" class="opacity-50">
                  {{ item.pin.status }}
                </span>
              </template>
            </template>
            <template #item.commitSha="{ item }">
              <span
                class="font-mono text-sm"
                :title="item.commitSha"
                v-text="item.commitSha"
              />
            </template>
            <template #item.updatedAt="{ item }">
              <span
                class="text-sm"
                v-text="formatTimestamp(item.updatedAt)"
              />
            </template>
            <template #item.createdAt="{ item }">
              <span
                class="text-sm"
                v-text="formatTimestamp(item.createdAt)"
              />
            </template>
            <template #item.actions="{ item }">
              <v-menu>
                <template #activator="{ props }">
                  <v-btn v-bind="props" variant="text" icon="mdi-dots-vertical" />
                </template>
                <v-list>
                  <v-list-item
                    :disabled="!!item.pin"
                    @click="pinVersion(item.tag)"
                  >
                    <v-list-item-title v-if="item.pin?.status === 'failed'" key="failed-pinning">
                      Retry pin v{{ item.version }} to {{ currentEnvironment?.name }} env...
                    </v-list-item-title>
                    <v-list-item-title v-else-if="!!item.pin && item.pin.status !== 'failed'" key="already-pinned">
                      Version is already pinned
                    </v-list-item-title>
                    <v-list-item-title v-else key="pin-version">
                      Pin v{{ item.version }} to {{ currentEnvironment?.name }} env...
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <v-card color="darknavyblue" elevation="5" class="pa-4 mb-4 overflow-visible">
        <v-card-title>Global Program Metadata</v-card-title>
        <v-card-text>
          <v-progress-circular v-if="loading" indeterminate />
          <p v-else-if="!localProgram" class="opacity-50">
            Program not found.
          </p>
          <template v-else>
            <div class="mb-4 text-base">
              <p><strong>Metadata</strong></p>
              <ProgramMetadataField
                ref="metadataField"
                v-model="localProgram.metadata"
                :message="ProgramMetadata"
              />
            </div>
            <div class="d-flex justify-end ga-2">
              <v-btn
                v-if="loadedProgram"
                variant="text"
                :disabled="loading || saving || !hasUnsavedChanges"
                @click="resetForm(loadedProgram)"
              >
                Reset
              </v-btn>
              <v-btn
                color="primary"
                :loading="saving"
                :disabled="actionDisabled"
                @click="saveProgram"
              >
                Update Global Metadata
              </v-btn>
            </div>
          </template>
        </v-card-text>
      </v-card>

      <ProgramEnvironmentMetadata
        :program="localProgram"
      />
    </div>
  </v-container>
</template>

<script lang="ts">
import { Program, ProgramMetadata } from '@policyfly/protobuf/pfbingo'
import { storeToRefs } from 'pinia'
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  ref,
  shallowRef,
  useTemplateRef,
  watch,
} from 'vue'
import { useRoute } from 'vue-router'

import ProgramEnvironmentMetadata from '@/components/ProgramEnvironmentMetadata.vue'
import ProgramMetadataField from '@/components/ProgramMetadataField.vue'
import { useApiStore } from '@/stores/api'
import { useDomainStore } from '@/stores/domain'
import { useEnvironmentStore } from '@/stores/environment'
import { useProgramStore } from '@/stores/program'
import { useReleaseStore } from '@/stores/release'

import { RouteNames } from '@/router'
import { canPinReleaseVersion } from '@/utils/program'

import type { Environment, ReadUserMembershipsResponse_ProgramContext } from '@policyfly/protobuf/pfbingo'
import type { ReleasePin, Version } from '@policyfly/protobuf/pfrelease'
import type { RouteLocationRaw } from 'vue-router'
import type { DataTableHeader } from 'vuetify'

interface VersionTableRow {
  tag: string
  env: Environment | undefined
  pin: ReleasePin | undefined
  version: string
  commitSha: string
  createdAt: number
  updatedAt: number | undefined
}

const PIN_REFRESH_INTERVAL = 10_000

export default defineComponent({
  name: 'Program',

  components: {
    ProgramMetadataField,
    ProgramEnvironmentMetadata,
  },

  setup () {
    const apiStore = useApiStore()
    const programStore = useProgramStore()
    const route = useRoute()
    const release = useReleaseStore()
    const { currentDomain } = storeToRefs(useDomainStore())
    const { currentEnvironment, environments } = storeToRefs(useEnvironmentStore())

    const programId = computed(() => route.params.id as string)
    const metadataField = useTemplateRef<typeof ProgramMetadataField>('metadataField')
    const loading = ref(true)
    const saving = ref(false)
    const loadedProgram = ref<Program | null>(null)
    const localProgram = ref<Program | null>(null)
    const errorMessage = ref('')
    const successMessage = ref('')

    // Program versioning
    const pinning = ref(false)
    const loadedVersions = ref<Version[]>([])
    const userMemberships = shallowRef<ReadUserMembershipsResponse_ProgramContext[]>([])
    const loadedPins = computed<ReleasePin[]>(() => {
      const slug = localProgram.value?.slug
      if (!slug) return []
      return release.pinsForSlug(slug).value
    })
    const pendingPins = computed(() => {
      return loadedPins.value.filter((pin) => !['failed', 'synced'].includes(pin.status))
    })
    const currentProgramMembership = computed(() => {
      const currentProgramId = localProgram.value?.id
      if (!currentProgramId) return null
      return userMemberships.value.find((membership) => {
        return membership.programId === currentProgramId || membership.program?.id === currentProgramId
      }) ?? null
    })
    const canPinVersion = computed(() => {
      return canPinReleaseVersion(currentProgramMembership.value?.role, currentEnvironment.value?.name)
    })
    const versionHeaders = computed<DataTableHeader<VersionTableRow>[]>(() => {
      return [
        { title: 'Version', key: 'version', value: 'version', sortable: true },
        { title: 'Status', key: 'status', value: 'pin.status', sortable: false },
        { title: 'Updated', key: 'updatedAt', value: 'updatedAt', sortable: true },
        { title: 'Created', key: 'createdAt', value: 'createdAt', sortable: true },
        ...(canPinVersion.value
          ? [{ title: 'Actions', key: 'actions', value: '', sortable: false, align: 'end' } as DataTableHeader<VersionTableRow>]
          : []),
      ]
    })
    const versionRows = computed<VersionTableRow[]>(() => {
      return loadedVersions.value
        .map((version) => {
          const pin = loadedPins.value.find(({ tag }) => tag === version.tag)
          const env = environments.value.find((e) => e.id === pin?.env)
          return {
            env,
            pin,
            tag: version.tag,
            version: version.version,
            commitSha: version.commitSha,
            createdAt: getTimestampMs(version.createdAt),
            updatedAt: pin ? getTimestampMs(pin?.updatedAt) : undefined,
          }
        })
        .sort((a, b) => b.createdAt - a.createdAt)
    })

    function envChipColor (env: Environment | undefined): string {
      switch (env?.name) {
        case 'dev':
          return 'bg-blue-500'
        case 'stage':
          return 'bg-purple-700'
        case 'bugfix':
          return 'bg-yellow-600'
        case 'prod':
          return 'bg-green-700'
        default:
          return 'bg-gray-500'
      }
    }

    function versionRoute (tag: string): RouteLocationRaw {
      return { name: RouteNames.PROGRAM_VERSION, params: { id: programId.value, tag } }
    }

    async function pinVersion (tag: string): Promise<void> {
      if (!canPinVersion.value || !localProgram.value || !currentEnvironment.value || pinning.value) return
      if (!confirm(`Are you sure you want to pin ${localProgram.value.slug.toLocaleUpperCase()} v${tag} to the ${currentEnvironment.value.name} environment?`)) return
      pinning.value = true
      errorMessage.value = ''
      successMessage.value = ''
      try {
        await release.pinVersion(localProgram.value.slug, tag)
        successMessage.value = `Pinned v${tag} to ${currentEnvironment.value.name} environment.`
      } catch (err) {
        console.error(err)
        errorMessage.value = err instanceof Error ? err.message : 'Failed to pin version.'
      } finally {
        pinning.value = false
      }
    }

    const refreshInterval = ref<number | null>(null)
    function stopRefreshInterval (): void {
      if (!refreshInterval.value) return
      window.clearInterval(refreshInterval.value)
      refreshInterval.value = null
    }
    function startRefreshInterval (): void {
      if (refreshInterval.value) return
      refreshInterval.value = window.setInterval(() => {
        if (!pendingPins.value.length) {
          stopRefreshInterval()
          return
        }
        release.loadMatrix()
      }, PIN_REFRESH_INTERVAL)
    }
    watch(pendingPins, (pending) => {
      if (pending.length) startRefreshInterval()
      else stopRefreshInterval()
    }, { immediate: true })
    onBeforeUnmount(stopRefreshInterval)

    async function loadCurrentUserMemberships (userId: string, environmentId: string): Promise<void> {
      try {
        const { memberships } = await apiStore.loadUserMemberships({
          userId,
          environmentId,
          domain: currentDomain.value,
        })
        userMemberships.value = Object.values(memberships)
      } catch (err) {
        console.error(err)
        userMemberships.value = []
      }
    }

    function resetForm (program: Program): void {
      loadedProgram.value = program
      localProgram.value = Program.clone(program)
      errorMessage.value = ''
      successMessage.value = ''
    }

    function getTimestampMs (timestamp?: Version['createdAt']): number {
      if (!timestamp) return -1
      const seconds = Number(timestamp.seconds)
      if (Number.isNaN(seconds)) return -1
      return (seconds * 1000) + Math.floor(timestamp.nanos / 1_000_000)
    }

    function formatTimestamp (ms?: number): string {
      if (ms === undefined || ms < 0) return ''
      return new Intl.DateTimeFormat('en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(ms))
    }

    watch(programId, async (id) => {
      loading.value = true
      try {
        const program = await loadProgram(id)
        await loadVersions(program.slug)
      } finally {
        loading.value = false
      }
    }, { immediate: true })

    watch(() => ({
      domain: currentDomain.value,
      environmentId: currentEnvironment.value?.id,
      userId: apiStore.userId,
    }), async ({ environmentId, userId }) => {
      if (!environmentId || !userId) {
        userMemberships.value = []
        return
      }
      await loadCurrentUserMemberships(userId, environmentId)
    }, { immediate: true })

    async function loadProgram (id: string): Promise<Program> {
      try {
        const { program } = await apiStore.loadProgramDetails(id)
        if (!program) throw new Error('Program not found.')
        resetForm(program)
        return program
      } catch (err) {
        console.error(err)
        errorMessage.value = err instanceof Error ? err.message : 'Failed to load program.'
        loadedProgram.value = null
        localProgram.value = null
        throw err
      }
    }

    async function loadVersions (slug: string): Promise<void> {
      try {
        const { versions } = await apiStore.listVersionsForProgram({ slug })
        loadedVersions.value = versions
      } catch (err) {
        console.error(err)
        errorMessage.value = err instanceof Error ? err.message : 'Failed to load program versions.'
        loadedVersions.value = []
      }
    }

    const hasUnsavedChanges = computed(() => {
      return !Program.equals(localProgram.value ?? undefined, loadedProgram.value ?? undefined)
    })
    const actionDisabled = computed(() => {
      return loading.value || saving.value || !hasUnsavedChanges.value || !metadataField.value?.valid
    })

    async function saveProgram (): Promise<void> {
      if (!localProgram.value) return
      saving.value = true
      errorMessage.value = ''
      successMessage.value = ''
      try {
        const { program } = await apiStore.createProgram({
          program: localProgram.value,
        })
        resetForm(program!)
        successMessage.value = `Saved metadata for "${program!.name}".`
        await programStore.loadPrograms()
      } catch (err) {
        console.error(err)
        errorMessage.value = err instanceof Error ? err.message : 'Failed to save program metadata.'
      } finally {
        saving.value = false
      }
    }

    return {
      ProgramMetadata,
      loading,
      saving,
      loadedPins,
      loadedProgram,
      loadedVersions,
      pendingPins,
      localProgram,
      errorMessage,
      successMessage,
      versionHeaders,
      versionRows,
      canPinVersion,
      hasUnsavedChanges,
      actionDisabled,
      currentEnvironment,
      formatTimestamp,
      envChipColor,
      versionRoute,
      pinVersion,
      resetForm,
      saveProgram,
    }
  },
})
</script>
